import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LockClosedIcon, DevicePhoneMobileIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import API_URL from '../../config/api';
import PublicHeader from '../../components/public/PublicHeader';
import DialCodePhoneInput from '../../components/Patients/DialCodePhoneInput';
import { DEMO_PRICES } from '../../utils/demoPricing';
import mtnLogo from '../../assets/images/MTN.png';
import airtelLogo from '../../assets/images/AIRTEL.png';

// ─── Order Summary Breakdown ─────────────────────────────────────────────
const OrderSummary = ({ displayCurrency, displayAmount, numericAmount, finalAmount, discount, couponApplied, vatRate, hasVat, usdEquivalent, showCoupon, setShowCoupon, couponCode, setCouponCode, setCouponApplied, setCouponError, couponError, itemLabel = 'Demo Access' }) => {
  const applyCoupon = (e) => {
    e.preventDefault();
    if (!couponCode.trim()) { setCouponError('Enter a code'); return; }
    // Future: validate coupon against backend
    setCouponError('Invalid or expired code');
    setCouponApplied(false);
  };

  return (
    <div className="pt-2 space-y-2">
      {/* Line items */}
      <div className="space-y-1.5 px-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">{itemLabel}</span>
          <span className="text-gray-700 font-medium">{displayCurrency} {displayAmount}</span>
        </div>

        {/* Processing fee */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">Processing Fee</span>
          <span className="text-gray-400">
            <span className="relative inline-block mr-1">
              3%
              <span className="absolute left-0 right-0 top-1/2 h-px bg-gray-400 -rotate-12 origin-center"></span>
            </span>
            <span className="text-teal-600 font-medium">Free</span>
          </span>
        </div>

        {/* VAT */}
        {hasVat && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">VAT ({vatRate}%)</span>
            <span className="text-gray-400 font-medium">Included</span>
          </div>
        )}

        {/* Discount */}
        {couponApplied && discount > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-teal-600">Discount</span>
            <span className="text-teal-600 font-medium">- {displayCurrency} {discount.toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-100 mx-1"></div>

      {/* Total */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-semibold text-gray-600">Total</span>
        <div className="text-right">
          <span className="text-sm font-bold text-gray-900">{displayCurrency} {finalAmount.toLocaleString()}</span>
          {displayCurrency !== 'USD' && (
            <p className="text-[10px] text-gray-400">≈ ${usdEquivalent} USD</p>
          )}
        </div>
      </div>

      {/* Coupon */}
      <div className="px-1 pt-1">
        {!showCoupon ? (
          <button
            type="button"
            onClick={() => setShowCoupon(true)}
            className="text-[11px] text-teal-600 hover:text-teal-700 font-medium"
          >
            Have a promo code?
          </button>
        ) : (
          <div className="flex gap-2">
            <form onSubmit={applyCoupon} className="flex gap-2 flex-1">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
                  placeholder="Enter promo code"
                  className="w-full pl-3 pr-8 py-2 text-xs rounded-lg border border-gray-200 bg-white text-gray-900 outline-none focus:border-teal-500 transition-all"
                />
                <button
                  type="button"
                  onClick={() => { setShowCoupon(false); setCouponCode(''); setCouponError(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <button
                type="submit"
                className="px-3 py-2 text-xs font-semibold text-white rounded-lg bg-teal-500 hover:bg-teal-600 transition-all"
              >
                Apply
              </button>
            </form>
          </div>
        )}
        {couponError && <p className="text-[10px] text-red-500 mt-1">{couponError}</p>}
        {couponApplied && <p className="text-[10px] text-teal-600 mt-1">Promo applied successfully!</p>}
      </div>
    </div>
  );
};

// ─── Stripe Card Form (mounted inside <Elements>) ────────────────────────
// Raw card details go straight to Stripe — they never touch our server.
function StripeCardForm({ onConfirmed, onError, disabled }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });
    if (error) {
      onError(error.message || 'Card payment failed');
      setSubmitting(false);
      return;
    }
    // 'succeeded' → done; 'processing'/'requires_capture' → poll the backend
    onConfirmed(paymentIntent?.status);
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!ready && (
        <div className="flex items-center justify-center py-6">
          <svg className="animate-spin h-5 w-5 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="ml-2 text-xs text-gray-400">Loading secure card form…</span>
        </div>
      )}
      <PaymentElement
        onReady={() => setReady(true)}
        onLoadError={(e) => onError(e?.error?.message || 'Failed to load the card form. Please try again.')}
      />
      {ready && (
        <button
          type="submit"
          disabled={!stripe || submitting || disabled}
          className="mx-auto w-full py-3 px-10 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 hover:shadow-lg hover:shadow-teal-500/20 transition-all duration-200 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing…
            </>
          ) : (
            <>
              <LockClosedIcon className="h-4 w-4" />
              Pay Now
            </>
          )}
        </button>
      )}
    </form>
  );
}

// ─── Component ───────────────────────────────────────────────────────────
function parseApiError(data, fallback) {
  const detail = data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => {
      const loc = (e.loc || []).filter(l => l !== 'body').join('.');
      return loc ? `${loc}: ${e.msg}` : e.msg;
    }).join(', ');
  }
  if (typeof detail === 'object') return detail.message || JSON.stringify(detail);
  return fallback;
}

const SecureCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const txRef = searchParams.get('tx_ref') || '';
  const firstName = searchParams.get('first_name') || '';
  const lastName = searchParams.get('last_name') || '';
  const email = searchParams.get('email') || '';
  const country = searchParams.get('country') || '';
  const phone = searchParams.get('phone') || '';
  const city = searchParams.get('city') || '';
  const source = searchParams.get('source') || 'demo'; // 'demo', 'subscription', or 'renewal'
  const isSubscription = source === 'subscription' || source === 'renewal';
  const planName = searchParams.get('plan') || 'Premium';
  const billingPeriod = searchParams.get('billing_period') || 'monthly';
  const urlAmount = searchParams.get('amount') || '';
  const urlCurrency = searchParams.get('currency') || '';
  const urlUsdAmount = searchParams.get('usd_amount') || '';

  // Single source of truth: DEMO_PRICES from utils/demoPricing.js.
  const COUNTRY_CODES = {
    'Uganda': '256', 'Kenya': '254', 'Nigeria': '234', 'Ghana': '233',
    'Tanzania': '255', 'Rwanda': '250', 'South Africa': '27',
    'United Kingdom': '44', 'United States': '1',
  };
  const COUNTRY_CURRENCY_MAP = Object.fromEntries(
    Object.entries(DEMO_PRICES).map(([c, p]) => [
      c,
      {
        currency: p.currency,
        amount: p.amount >= 100 ? p.amount.toLocaleString() : p.amount.toString(),
        countryCode: COUNTRY_CODES[c] || '256',
      },
    ])
  );
  const detected = COUNTRY_CURRENCY_MAP[country] || { currency: 'USD', amount: '2', countryCode: '256' };
  // For subscriptions, use the amount/currency from the URL (passed by the
  // SubscriptionModal / backend checkout link). For demos, use DEMO_PRICES.
  const displayCurrency = isSubscription ? (urlCurrency || 'USD') : detected.currency;
  const displayAmount = isSubscription ? (urlAmount || '0') : detected.amount;

  // DGateway mobile-money rails only cover UGX/KES/TZS/RWF — everything else
  // pays by card. Gate the MoMo tabs on the display currency.
  const MOMO_CURRENCIES = ['UGX', 'KES', 'TZS', 'RWF'];
  const momoAvailable = MOMO_CURRENCIES.includes((displayCurrency || '').toUpperCase());

  const [paymentMethod, setPaymentMethod] = useState(momoAvailable ? 'mobile_money_mtn' : 'card');
  const [mobileNetwork, setMobileNetwork] = useState('MTN');
  const [mobilePhone, setMobilePhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [awaitingPrompt, setAwaitingPrompt] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [showCoupon, setShowCoupon] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);

  // Stripe session state — created by POST /charge-card, consumed by Elements
  const [stripeClientSecret, setStripeClientSecret] = useState('');
  const [stripePromise, setStripePromise] = useState(null);

  const pollTimer = useRef(null);
  const pollAttempts = useRef(0);

  // VAT rates by country
  const VAT_RATES = {
    'Uganda': 18, 'Kenya': 16, 'Nigeria': 7.5, 'Ghana': 15,
    'Tanzania': 18, 'Rwanda': 18, 'South Africa': 15,
    'United Kingdom': 20, 'United States': 0,
  };
  const vatRate = VAT_RATES[country] || 0;
  const hasVat = vatRate > 0;

  // USD equivalent for display
  const USD_EQUIVALENTS = {
    'UGX': 0.000263, 'KES': 0.0077, 'NGN': 0.00067,
    'GHS': 0.083, 'TZS': 0.0004, 'RWF': 0.00078,
    'ZAR': 0.053, 'GBP': 1.27, 'USD': 1,
  };
  const usdRate = USD_EQUIVALENTS[displayCurrency] || 1;
  const numericAmount = parseFloat((displayAmount || '0').replace(/,/g, ''));
  const usdEquivalent = (isSubscription && urlUsdAmount)
    ? parseFloat(urlUsdAmount).toFixed(2)
    : (numericAmount * usdRate).toFixed(2);

  const discount = couponApplied ? 0 : 0;
  const finalAmount = numericAmount - discount;

  const verifyUrl = isSubscription
    ? `${API_URL}/api/payments/verify-charge?tx_ref=${encodeURIComponent(txRef)}`
    : `${API_URL}/api/demo/verify-charge?tx_ref=${encodeURIComponent(txRef)}`;

  const stopPolling = () => {
    if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
  };

  useEffect(() => () => stopPolling(), []);

  // Poll the backend every 10s (DGateway rate limits) for up to ~5 minutes.
  const poll = async () => {
    pollAttempts.current += 1;
    if (pollAttempts.current > 30) {
      setAwaitingPrompt(false);
      setError('Verification timed out. If you approved the prompt, your payment is still being confirmed — check back shortly or contact support.');
      setPaymentFailed(true);
      return;
    }
    try {
      const res = await fetch(verifyUrl);
      const data = await res.json();
      if (!res.ok) { setError(parseApiError(data, 'Could not verify payment.')); return; }
      if (data.payment_status === 'successful') {
        setAwaitingPrompt(false);
        navigate(`/payment/success?tx_ref=${txRef}&${isSubscription ? 'subscription=true' : 'demo=true'}`);
        return;
      }
      if (data.payment_status === 'failed' || data.payment_status === 'cancelled') {
        setAwaitingPrompt(false);
        setError(data.failure_reason || 'Payment was not successful.');
        setPaymentFailed(true);
        return;
      }
    } catch { /* transient — keep polling */ }
    pollTimer.current = setTimeout(poll, 10000);
  };

  const startPolling = () => {
    stopPolling();
    pollAttempts.current = 0;
    pollTimer.current = setTimeout(poll, 10000);
  };

  // Auto-verify when returning to the callback route (e.g. after a 3DS
  // redirect that used the return_url).
  useEffect(() => {
    const verifyCharge = async () => {
      if (!txRef) return;
      const status = searchParams.get('status');
      if (status === 'cancelled') { setError('Payment was cancelled.'); return; }
      setVerifying(true);
      try {
        const res = await fetch(verifyUrl);
        const data = await res.json();
        if (!res.ok) { setError(parseApiError(data, 'Could not verify payment.')); return; }
        if (data.payment_status === 'successful') navigate(`/payment/success?tx_ref=${txRef}&${isSubscription ? 'subscription=true' : 'demo=true'}`);
        else if (data.payment_status === 'failed' || data.payment_status === 'cancelled') { setError(data.failure_reason || 'Payment was not successful.'); setPaymentFailed(true); }
        else startPolling();
      } catch { setError('Could not verify payment.'); }
      finally { setVerifying(false); }
    };
    const isCallback = window.location.pathname.includes('/payment/dgateway/callback');
    if (isCallback && txRef) verifyCharge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txRef, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!txRef) { setError('Missing transaction reference.'); return; }
    if (paymentMethod === 'card') await handleCardPayment();
    else { setMobileNetwork(paymentMethod === 'mobile_money_airtel' ? 'AIRTEL' : 'MTN'); await handleMobileMoneyPayment(); }
  };

  // Card: ask our backend to create the DGateway collect (Stripe rail) —
  // the response carries a Stripe client_secret the browser confirms below.
  const handleCardPayment = async () => {
    setLoading(true);
    try {
      const chargeUrl = isSubscription
        ? `${API_URL}/api/payments/charge-card`
        : `${API_URL}/api/demo/charge-card`;
      const res = await fetch(chargeUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tx_ref: txRef, first_name: firstName, last_name: lastName,
          email, country, phone, city,
          currency: displayCurrency, amount: parseFloat((displayAmount || '0').replace(/,/g, '')),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(parseApiError(data, 'Card charge failed'));

      if (data.payment_status === 'successful') {
        navigate(`/payment/success?tx_ref=${txRef}&${isSubscription ? 'subscription=true' : 'demo=true'}`);
        return;
      }
      if (data.payment_status === 'failed') {
        setError('Card payment was declined.');
        setPaymentFailed(true);
        return;
      }

      const pubKey = data.stripe_publishable_key;
      const secret = data.client_secret;
      if (!pubKey || !secret) {
        setError('Card payments are not configured. Please try mobile money or contact support.');
        setPaymentFailed(true);
        return;
      }
      setStripeClientSecret(secret);
      setStripePromise(loadStripe(pubKey));
    } catch (err) {
      const msg = err.message || 'Payment failed.';
      setError(msg);
      if (/already exists|not valid|already been completed|not found/i.test(msg)) {
        setPaymentFailed(true);
      }
    }
    finally { setLoading(false); }
  };

  // Stripe confirm resolved → hand off to backend polling for final status.
  const handleStripeConfirmed = () => {
    startPolling();
    setAwaitingPrompt(true);
  };

  const handleMobileMoneyPayment = async () => {
    const phoneDigits = mobilePhone.replace(/\D/g, '');
    if (phoneDigits.length < 10) { setError('Enter a valid phone number.'); return; }
    setLoading(true);
    try {
      const momoUrl = isSubscription
        ? `${API_URL}/api/payments/charge-mobile-money`
        : `${API_URL}/api/demo/charge-mobile-money`;
      const res = await fetch(momoUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tx_ref: txRef, network: mobileNetwork, phone_number: phoneDigits,
          country_code: detected.countryCode, first_name: firstName, last_name: lastName,
          email, country, currency: displayCurrency, amount: parseFloat((displayAmount || '0').replace(/,/g, '')),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(parseApiError(data, 'Mobile money charge failed'));
      if (data.payment_status === 'successful') { navigate(`/payment/success?tx_ref=${txRef}&${isSubscription ? 'subscription=true' : 'demo=true'}`); return; }
      if (data.payment_status === 'failed') { setError('Mobile money payment was declined.'); setPaymentFailed(true); return; }
      // Prompt sent — poll while the customer approves on their phone
      setAwaitingPrompt(true);
      startPolling();
    } catch (err) {
      const msg = err.message || 'Payment failed.';
      setError(msg);
      if (/already exists|not valid|already been completed|not found/i.test(msg)) {
        setPaymentFailed(true);
      }
    }
    finally { setLoading(false); }
  };

  // ─── Payment methods ──────────────────────────────────────────────────
  const methods = [
    { id: 'card', tooltip: 'Visa, Mastercard, Amex — powered by Stripe', logo: <div className="flex items-center gap-1.5"><CreditCardIcon className="h-5 w-5 text-gray-600" /><span className="text-[10px] font-bold text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-1 rounded leading-none">CARD</span></div> },
    ...(momoAvailable ? [
      { id: 'mobile_money_mtn', tooltip: 'MTN Mobile Money', logo: <img src={mtnLogo} alt="MTN" className="h-[26px]" /> },
      { id: 'mobile_money_airtel', tooltip: 'Airtel Money', logo: <img src={airtelLogo} alt="Airtel" className="h-[24px]" /> },
    ] : []),
  ];

  // ─── Verifying state ──────────────────────────────────────────────────
  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-teal-50 rounded-full mb-4">
            <svg className="animate-spin h-7 w-7 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-gray-800 mb-1">Verifying payment…</h2>
          <p className="text-xs text-gray-400">Please wait while we confirm.</p>
        </div>
      </div>
    );
  }

  // ─── Payment failed state ─────────────────────────────────────────────
  if (paymentFailed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-red-50 rounded-full mb-4">
            <svg className="h-7 w-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-gray-800 mb-2">Payment could not be completed</h2>
          <p className="text-xs text-gray-500 mb-1">{error}</p>
          <p className="text-xs text-gray-400 mb-6">This payment session has expired. Please start a new {isSubscription ? 'subscription' : 'demo signup'} to get a fresh checkout.</p>
          <button
            onClick={() => navigate(isSubscription ? '/pricing' : '/?demo=1')}
            className="w-full py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl hover:from-teal-600 hover:to-cyan-600 transition-all mb-2"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <PublicHeader />

      {/* Subtle ambient glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[420px] bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,_var(--tw-gradient-stops))] from-teal-100/80 via-slate-200/40 to-transparent"></div>
      </div>

      <div className="relative pt-28 pb-16 flex justify-center px-4">
        <div className="w-full max-w-[620px]">
          {/* ─── Checkout Title ──────────────────────────────────────────── */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-slate-700 to-gray-900">Secure</span>{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-cyan-400">Checkout</span>
            </h1>
            <div className="h-1.5 w-20 bg-gradient-to-r from-teal-500 to-cyan-500 mx-auto rounded-full shadow-lg shadow-teal-500/30"></div>
          </div>

          {/* ─── Paying As ─────────────────────────────────────────────── */}
          <div className="mb-4 px-1 text-center">
            <p className="text-xs text-gray-500 leading-relaxed">
              Paying as{' '}
              <span className="text-gray-700 font-medium">{[firstName, lastName].filter(Boolean).join(' ')}</span>
              {' · '}
              <span className="text-gray-700">{email}</span>
              {country && <><span className="text-gray-400"> · </span><span className="text-gray-700">{country}</span></>}
            </p>
          </div>

          {/* ─── Checkout Card ─────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_8px_40px_rgba(0,0,0,0.10)] overflow-hidden">
            <div className="px-6 pt-6 pb-5">
              {/* Test mode badge + Order summary on same line */}
              <div className="flex items-center justify-between mb-5 pb-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-gray-900">{isSubscription ? `${planName} Subscription` : '7-Day Demo Access'}</p>
                    <p className="text-xs text-gray-400">{isSubscription ? `${planName} plan · ${billingPeriod}` : 'Premium plan · 7 days'}</p>
                  </div>
                </div>
                <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                  {isSubscription ? 'Checkout' : 'Test Mode'}
                </span>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600 flex items-start gap-2">
                  <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  <span className="flex-1">{error}</span>
                  <button
                    type="button"
                    onClick={() => setError('')}
                    className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                    aria-label="Dismiss error"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* ─── Payment Method Pills ─────────────────────────────── */}
              <div className="mb-5">
                <div className={`grid ${methods.length === 3 ? 'grid-cols-3' : 'grid-cols-1'} gap-3 mb-3 max-w-[90%] mx-auto`}>
                  {methods.map((m) => {
                    const active = paymentMethod === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        title={m.tooltip}
                        onClick={() => { setPaymentMethod(m.id); setError(''); }}
                        className={`relative flex items-center justify-center h-[60px] rounded-lg transition-all duration-200 ${
                          active
                            ? 'bg-teal-50 ring-1 ring-teal-500/30'
                            : 'bg-gray-50 hover:bg-gray-100 ring-1 ring-transparent'
                        }`}
                      >
                        {m.logo}
                        {active && (
                          <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center shadow-sm">
                            <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {!momoAvailable && (
                  <p className="text-[10px] text-gray-400 text-center -mt-1">
                    Mobile money isn't available in {displayCurrency} — please pay by card.
                  </p>
                )}
              </div>

              {/* ─── Card: Stripe Elements ──────────────────────────────── */}
              {paymentMethod === 'card' && (
                <div className="space-y-4">
                  <p className="text-[11px] text-gray-400 -mt-1">
                    Your card details go directly to Stripe — they never touch our servers.
                  </p>

                  {awaitingPrompt ? (
                    <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                      <svg className="animate-spin h-4 w-4 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Confirming your payment…
                    </div>
                  ) : stripeClientSecret && stripePromise ? (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret: stripeClientSecret,
                        appearance: { theme: 'stripe', variables: { colorPrimary: '#14b8a6' } },
                      }}
                    >
                      <StripeCardForm
                        onConfirmed={handleStripeConfirmed}
                        onError={(msg) => setError(msg)}
                        disabled={!agreedTerms}
                      />
                    </Elements>
                  ) : null}

                  {/* Amount + Pay button */}
                  <OrderSummary
                    displayCurrency={displayCurrency} displayAmount={displayAmount}
                    numericAmount={numericAmount} finalAmount={finalAmount}
                    discount={discount} couponApplied={couponApplied}
                    vatRate={vatRate} hasVat={hasVat} usdEquivalent={usdEquivalent}
                    showCoupon={showCoupon} setShowCoupon={setShowCoupon}
                    couponCode={couponCode} setCouponCode={setCouponCode}
                    setCouponApplied={setCouponApplied} setCouponError={setCouponError}
                    couponError={couponError}
                    itemLabel={isSubscription ? 'Subscription' : 'Demo Access'}
                  />

                  {/* Terms checkbox */}
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedTerms}
                      onChange={(e) => setAgreedTerms(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-teal-500 focus:ring-teal-400"
                    />
                    <span className="text-[11px] text-gray-500 leading-relaxed">
                      I agree to the <span className="text-teal-600 underline">Terms of Service</span> and acknowledge that {isSubscription ? 'subscription payments' : 'demo access'} are non-refundable.
                    </span>
                  </label>

                  {!stripeClientSecret && !awaitingPrompt && (
                    <div className="pt-3">
                      <button
                        type="button"
                        onClick={handleCardPayment}
                        disabled={loading || !agreedTerms}
                        className="mx-auto w-full py-3 px-10 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 hover:shadow-lg hover:shadow-teal-500/20 transition-all duration-200 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Preparing card payment…
                          </>
                        ) : (
                          <>
                            <LockClosedIcon className="h-4 w-4" />
                            Continue to Card Details — {displayCurrency} {finalAmount.toLocaleString()}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ─── Mobile Money Form ──────────────────────────────────── */}
              {(paymentMethod === 'mobile_money_mtn' || paymentMethod === 'mobile_money_airtel') && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="label-rx">Phone Number</label>
                    <DialCodePhoneInput
                      name="mobile_phone"
                      country={country}
                      value={mobilePhone}
                      onChange={(val) => { setMobilePhone(val); setError(''); }}
                      placeholder="e.g. 755 569 649"
                    />
                  </div>

                  {awaitingPrompt ? (
                    <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-600 bg-teal-50/60 border border-teal-100 rounded-lg px-3">
                      <svg className="animate-spin h-4 w-4 text-teal-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span>Check your phone and approve the prompt.</span>
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-400 -mt-1">
                      You'll receive a payment prompt on your phone.
                    </p>
                  )}

                  {/* Amount + Pay button */}
                  <OrderSummary
                    displayCurrency={displayCurrency} displayAmount={displayAmount}
                    numericAmount={numericAmount} finalAmount={finalAmount}
                    discount={discount} couponApplied={couponApplied}
                    vatRate={vatRate} hasVat={hasVat} usdEquivalent={usdEquivalent}
                    showCoupon={showCoupon} setShowCoupon={setShowCoupon}
                    couponCode={couponCode} setCouponCode={setCouponCode}
                    setCouponApplied={setCouponApplied} setCouponError={setCouponError}
                    couponError={couponError}
                    itemLabel={isSubscription ? 'Subscription' : 'Demo Access'}
                  />
                  {/* Terms checkbox */}
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedTerms}
                      onChange={(e) => setAgreedTerms(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-teal-500 focus:ring-teal-400"
                    />
                    <span className="text-[11px] text-gray-500 leading-relaxed">
                      I agree to the <span className="text-teal-600 underline">Terms of Service</span> and acknowledge that {isSubscription ? 'subscription payments' : 'demo access'} are non-refundable.
                    </span>
                  </label>

                  {!awaitingPrompt && (
                    <div className="pt-3">
                      <button
                        type="submit"
                        disabled={loading || !agreedTerms}
                        className="mx-auto w-full py-3 px-10 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 hover:shadow-lg hover:shadow-teal-500/20 transition-all duration-200 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Sending prompt…
                          </>
                        ) : (
                          <>
                            <DevicePhoneMobileIcon className="h-4 w-4" />
                            Pay {displayCurrency} {finalAmount.toLocaleString()}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </form>
              )}
            </div>

            {/* ─── Trust Footer ─────────────────────────────────────────── */}
            <div className="px-6 py-3.5 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                <LockClosedIcon className="h-3 w-3" />
                <span>TLS encrypted</span>
              </div>
              <div className="text-[10px] text-gray-400 font-medium">
                Powered by <span className="text-gray-600 font-semibold">DGateway</span>
                <span className="text-gray-300"> · </span>
                <span className="text-gray-400">cards via <span className="text-gray-600 font-semibold">Stripe</span></span>
              </div>
            </div>
          </div>

          {/* ─── Micro trust text ───────────────────────────────────────── */}
          <p className="text-center text-xs text-gray-500 mt-4 px-6">
            Payments are processed securely by DGateway. Card details are handled by Stripe and never stored on our servers.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SecureCheckout;
